// Positioning for the @-mention candidate list.
//
// The list is portaled to document.body with `position: fixed` (so it escapes
// the comment box's `overflow: hidden` clip and the narrow drawer). These are
// the nominal box dimensions used to decide placement + clamp horizontally;
// the actual visual size still comes from MentionInputElement.module.scss.
export const MENTION_DROPDOWN_WIDTH_PX = 240;
export const MENTION_DROPDOWN_MAX_HEIGHT_PX = 224; // 14rem
export const MENTION_DROPDOWN_GAP_PX = 6;
const VIEWPORT_MARGIN_PX = 8;

export type MentionDropdownPlacement = 'above' | 'below';

export interface MentionDropdownPosition {
    left: number;
    top?: number;
    bottom?: number;
    placement: MentionDropdownPlacement;
}

interface AnchorRect {
    left: number;
    top: number;
    bottom: number;
}

interface Viewport {
    width: number;
    height: number;
}

interface DropdownSize {
    width: number;
    maxHeight: number;
}

/**
 * Place the candidate list relative to the inline @-input's bounding rect.
 *
 * Prefers opening ABOVE the input — the comment box sits at the bottom of a
 * narrow panel, so a downward list is clipped/hidden behind the footer. Flips
 * below only when there genuinely isn't room above. Returns `fixed`-style
 * coordinates (`bottom` for above, `top` for below) plus a horizontal `left`
 * clamped to the viewport so the list never overflows the right edge.
 */
export function computeMentionDropdownPosition(
    anchor: AnchorRect,
    viewport: Viewport,
    dropdown: DropdownSize = { width: MENTION_DROPDOWN_WIDTH_PX, maxHeight: MENTION_DROPDOWN_MAX_HEIGHT_PX },
    gap: number = MENTION_DROPDOWN_GAP_PX,
): MentionDropdownPosition {
    const spaceAbove = anchor.top;
    const spaceBelow = viewport.height - anchor.bottom;
    const placement: MentionDropdownPlacement = spaceAbove >= dropdown.maxHeight || spaceAbove >= spaceBelow ? 'above' : 'below';

    const maxLeft = viewport.width - dropdown.width - VIEWPORT_MARGIN_PX;
    const left = Math.max(VIEWPORT_MARGIN_PX, Math.min(anchor.left, maxLeft));

    if (placement === 'above') {
        return { left, bottom: viewport.height - anchor.top + gap, placement };
    }
    return { left, top: anchor.bottom + gap, placement };
}
